/**
 * Connecting MarketMapr to Claude, step by step with pictures (Alex's
 * guide, 4 Nov 2026). One list drives the picture walkthrough in
 * Settings → Integrations (ClaudeConnectGuide) and the Connect Claude
 * lesson on the Learn tab. Pictures live in public/guides/claude/ —
 * retake them when Claude's or our screens change.
 */
export interface ClaudeConnectStep {
  id: string;
  part: 1 | 2 | 3;
  title: string;
  body: string;
  image?: string;
  alt?: string;
  /** Offer the connector URL's copy button on this step (when it's known). */
  copyUrl?: boolean;
}

export const CLAUDE_CONNECT_PARTS: Record<1 | 2 | 3, string> = {
  1: 'In MarketMapr',
  2: 'In Claude',
  3: 'In Claude',
};

export const CLAUDE_CONNECT_STEPS: ClaudeConnectStep[] = [
  {
    id: 'key', part: 1,
    title: 'Create an API key',
    body: 'In MarketMapr, open Settings → Integrations. Under “Claude & API access”, give your key a name (for example “Claude”) and press Create API key.',
    image: '/guides/claude/1_integrations.png',
    alt: 'MarketMapr Settings, Integrations tab, with the Create API key button',
  },
  {
    id: 'url', part: 1,
    title: 'Copy your connector URL',
    body: 'Your new key appears in a green panel. Press Copy connector URL, the second green button. It’s only shown once, so keep the page open until it’s in Claude. If you lose it, create a new key.',
    image: '/guides/claude/2_newkey.png',
    alt: 'The new key panel with the Copy connector URL button',
    copyUrl: true,
  },
  {
    id: 'settings', part: 2,
    title: 'Open Claude’s settings',
    body: 'In Claude (claude.ai or the desktop app), click your name in the bottom-left corner, then Settings.',
    image: '/guides/claude/3_menu.png',
    alt: 'Claude account menu with Settings',
  },
  {
    id: 'connectors', part: 2,
    title: 'Add a custom connector',
    body: 'Go to Customize → Connectors, press + Add (top right) and choose Add custom connector.',
    image: '/guides/claude/4_connectors.png',
    alt: 'Claude Settings, Customize, Connectors, Add custom connector',
  },
  {
    id: 'paste', part: 2,
    title: 'Name it and paste the URL',
    body: 'Name it MarketMapr, paste your connector URL into the second box and press Continue.',
    image: '/guides/claude/5_addconnector.png',
    alt: 'Add custom connector: the name MarketMapr and the pasted URL',
    copyUrl: true,
  },
  {
    id: 'auth', part: 2,
    title: 'Leave “No sign-in” selected',
    body: 'Under Authentication, leave No sign-in selected (it’s picked for you) and press Add. Your key is inside the URL, so no sign-in is needed.',
    image: '/guides/claude/6_auth.png',
    alt: 'Authentication with No sign-in selected',
  },
  {
    id: 'allow', part: 3,
    title: 'Set permissions to Always allow',
    body: 'Open MarketMapr in your connector list. Under Tool permissions, open the Needs approval menu next to Other tools and choose Always allow, so Claude doesn’t stop to ask before every search.',
    image: '/guides/claude/7_permissions.png',
    alt: 'Tool permissions: choose Always allow',
  },
  {
    id: 'ask', part: 3,
    title: 'You’re connected: just ask',
    body: 'In any new chat, check MarketMapr is switched on in the tools menu under the message box, then ask in plain English: “How many senior planners are there in Nottingham?”, “Map the C# market in Leeds into a project”, “Who’s left the companies in my project this month, and who should I call about each backfill?”',
  },
  {
    id: 'refresh', part: 3,
    title: 'When we add new tools: refresh',
    body: 'Claude keeps the list of MarketMapr tools it loaded until you refresh it. When we add something new, you’ll see a blue “i” on Settings in MarketMapr (and Claude will mention it too). Open MarketMapr in Claude’s connectors, click ⋯ (top right) → Refresh tools list, then set the permissions to Always allow again. Using MarketMapr in ChatGPT as well? Refresh it there too.',
    image: '/guides/claude/8_refresh.png',
    alt: 'The MarketMapr connector in Claude, with the ⋯ menu circled',
  },
];
